import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { MessageSquareText } from "lucide-react";
import Seo, { breadcrumbs, faqSchema } from "../components/Seo";
import {
  PRODUCTS_BY_SLUG, PROTECTION_PRODUCTS, PROTECTION_DISCLAIMER, relatedProducts,
} from "../data/protectionProducts";
import { siteConfig } from "../config";

export default function ProtectionProductPage() {
  const { slug } = useParams();
  const product = PRODUCTS_BY_SLUG[slug];
  const [showAddedNote, setShowAddedNote] = useState(false);

  if (!product) return <Navigate to="/protection-products" replace />;

  const showPrice = product.slug === "paint-fabric-defense" || product.slug === "gap-protection";
  const Icon = product.Icon;

  const handleAddToDeal = () => {
    setShowAddedNote(true);
    setTimeout(() => {
      window.location.href = `/scotts-deal-desk?protection=${product.slug}`;
    }, 250);
  };

  return (
    <main id="main-content">
      <Seo
        title={product.seoTitle}
        description={product.metaDescription}
        canonicalPath={`/protection-products/${product.slug}`}
        jsonLd={[
          breadcrumbs([
            { name: "Home", path: "/" },
            { name: "Protection Products", path: "/protection-products" },
            { name: product.name, path: `/protection-products/${product.slug}` },
          ]),
          faqSchema(product.faqs),
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: product.name,
            description: product.metaDescription,
            url: `https://scottsellschevys.com/protection-products/${product.slug}`,
          },
        ]}
      />

      {/* Breadcrumb */}
      <nav className="bg-[var(--ssc-paper)] border-b border-[var(--ssc-line)]" aria-label="Breadcrumb">
        <div className="ssc-container py-3 text-xs text-[var(--ssc-ink-2)]" data-testid="product-breadcrumb">
          <Link to="/" className="hover:text-[var(--ssc-blue)]">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/protection-products" className="hover:text-[var(--ssc-blue)]">Protection Products</Link>
          <span className="mx-2">/</span>
          <span className="text-[var(--ssc-navy)] font-semibold">{product.name}</span>
        </div>
      </nav>

      {/* 1. Hero */}
      <section className="ssc-hero">
        <div className="ssc-container py-10 md:py-14">
          <div className="grid md:grid-cols-3 gap-6 items-start">
            <div className="md:col-span-2">
              <div className="text-[10px] font-extrabold uppercase tracking-[0.25em] text-[var(--ssc-silver)]">Optional protection</div>
              <h1 className="ssc-display text-3xl md:text-5xl font-extrabold mt-2">{product.name}</h1>
              <p className="mt-4 text-[var(--ssc-silver)] text-base md:text-lg leading-relaxed">
                {product.heroText}
              </p>
              {showPrice && (
                <div className="mt-5 inline-flex items-baseline gap-3 bg-white/10 border border-white/20 rounded-2xl px-5 py-3">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--ssc-silver)]">Price</span>
                  <span className="text-2xl md:text-3xl font-black" data-testid="product-price">
                    ${product.price.toLocaleString()}
                  </span>
                </div>
              )}
              {product.slug === "paint-fabric-defense" && (
                <p className="text-xs text-[var(--ssc-silver)] mt-3">
                  Ask Scott about available savings during your deal review.
                </p>
              )}
              <div className="mt-6 flex flex-wrap gap-2">
                <button onClick={handleAddToDeal} className="ssc-btn ssc-btn-primary"
                        data-testid="product-add-to-deal-hero">
                  Add to Build My Deal
                </button>
                <Link to="/contact" className="ssc-btn ssc-btn-outline-white"
                      data-testid="product-ask-scott-hero">
                  Ask Scott About This Product
                </Link>
                <Link to="/protection-products" className="ssc-btn ssc-btn-outline-white"
                      data-testid="product-back-hero">
                  Back to Protection Products
                </Link>
              </div>
            </div>
            <div className="hidden md:flex items-center justify-center">
              <div className="w-32 h-32 rounded-3xl bg-white/10 border border-white/20 grid place-items-center text-white">
                <Icon size={64} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="ssc-section">
        <div className="ssc-container max-w-4xl space-y-8">
          {showAddedNote && (
            <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-3 text-sm text-emerald-800"
                 data-testid="product-added-note">
              Taking you to Build My Own Deal…
            </div>
          )}

          {/* 2. What this product is */}
          <section className="ssc-card" data-testid="product-what-is">
            <h2 className="text-xl font-extrabold text-[var(--ssc-navy)]">What this product is</h2>
            <p className="mt-2 text-[var(--ssc-ink)] leading-relaxed">{product.whatItIs}</p>
          </section>

          {/* 3. What it may help with */}
          <section className="ssc-card" data-testid="product-may-help">
            <h2 className="text-xl font-extrabold text-[var(--ssc-navy)]">What it may help with</h2>
            <ul className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm text-[var(--ssc-ink)]">
              {product.mayHelpWith.map((m) => <li key={m}>• {m}</li>)}
            </ul>
          </section>

          {/* 4. Who it may be best for */}
          <section className="ssc-card bg-[var(--ssc-paper)]" data-testid="product-best-for">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--ssc-blue)]">Best for</div>
            <p className="mt-1 text-[var(--ssc-ink)] leading-relaxed">{product.bestFor}</p>
          </section>

          {/* 5. Common real-life examples */}
          {product.examples?.length > 0 && (
            <section className="ssc-card" data-testid="product-examples">
              <h2 className="text-xl font-extrabold text-[var(--ssc-navy)]">Common real-life examples</h2>
              <ul className="mt-3 space-y-2 text-sm text-[var(--ssc-ink)]">
                {product.examples.map((e, i) => <li key={i}>• {e}</li>)}
              </ul>
            </section>
          )}

          {/* 6. What to ask Scott */}
          {product.askScott?.length > 0 && (
            <section className="ssc-card" data-testid="product-ask-scott">
              <h2 className="text-xl font-extrabold text-[var(--ssc-navy)]">What to ask Scott</h2>
              <ul className="mt-3 space-y-2 text-sm text-[var(--ssc-ink)]">
                {product.askScott.map((q, i) => <li key={i}>• {q}</li>)}
              </ul>
              <Link to="/contact" className="ssc-btn ssc-btn-primary mt-4"
                    data-testid="product-ask-scott-cta">
                <MessageSquareText size={16} /> Ask Scott About This Product
              </Link>
            </section>
          )}

          {/* 7. Limits + middle disclaimer */}
          <section className="ssc-card border-2 border-amber-200 bg-amber-50/30" data-testid="product-limits">
            <h2 className="text-xl font-extrabold text-[var(--ssc-navy)]">Important limits and exclusions</h2>
            <p className="mt-2 text-sm text-[var(--ssc-ink)] leading-relaxed">{product.limits}</p>
            <p className="text-xs text-[var(--ssc-ink-2)] mt-3 leading-relaxed">
              {PROTECTION_DISCLAIMER}
            </p>
          </section>

          {/* Service Contract level cards */}
          {product.levels?.length > 0 && (
            <section className="ssc-card" data-testid="product-levels">
              <h2 className="text-xl font-extrabold text-[var(--ssc-navy)]">Coverage Levels</h2>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {product.levels.map((s) => {
                  const lvl = PRODUCTS_BY_SLUG[s];
                  if (!lvl) return null;
                  return (
                    <Link key={s} to={`/protection-products/${s}`}
                          className="block bg-[var(--ssc-paper)] rounded-xl p-4 border border-[var(--ssc-line)] hover:border-[var(--ssc-blue)] transition"
                          data-testid={`product-level-link-${s}`}>
                      <div className="flex items-center gap-2">
                        <lvl.Icon size={18} className="text-[var(--ssc-blue)]" />
                        <div className="font-extrabold text-[var(--ssc-navy)]">{lvl.name}</div>
                      </div>
                      <p className="text-xs text-[var(--ssc-ink-2)] mt-1">{lvl.shortDesc}</p>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* 5-in-1 included products */}
          {product.bundleIncludes?.length > 0 && (
            <section className="ssc-card" data-testid="product-bundle">
              <h2 className="text-xl font-extrabold text-[var(--ssc-navy)]">Included protections</h2>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {product.bundleIncludes.map((s) => {
                  const inc = PRODUCTS_BY_SLUG[s];
                  if (!inc) return null;
                  return (
                    <Link key={s} to={`/protection-products/${s}`}
                          className="block bg-[var(--ssc-paper)] rounded-xl p-4 border border-[var(--ssc-line)] hover:border-[var(--ssc-blue)] transition"
                          data-testid={`product-bundle-link-${s}`}>
                      <div className="flex items-center gap-2">
                        <inc.Icon size={18} className="text-[var(--ssc-blue)]" />
                        <div className="font-extrabold text-[var(--ssc-navy)]">{inc.name}</div>
                      </div>
                      <p className="text-xs text-[var(--ssc-ink-2)] mt-1">{inc.shortDesc}</p>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* 8. Add this to Build My Deal CTA */}
          <section className="ssc-card-dark text-center" data-testid="product-mid-cta">
            <h2 className="text-2xl font-extrabold">Want Scott to include this in your deal review?</h2>
            <p className="mt-2 text-[var(--ssc-silver)]">
              Add {product.name} to your Build My Own Deal request and Scott will go over the details with you.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <button onClick={handleAddToDeal} className="ssc-btn ssc-btn-primary"
                      data-testid="product-add-to-deal-mid">
                Add to Build My Deal
              </button>
              <Link to="/contact" className="ssc-btn ssc-btn-outline-white"
                    data-testid="product-ask-scott-mid">
                Ask Scott About This Product
              </Link>
            </div>
          </section>

          {/* 9. Related Products */}
          <section data-testid="product-related">
            <h2 className="text-xl font-extrabold text-[var(--ssc-navy)] mb-3">Related Protection Products</h2>
            <div className="grid sm:grid-cols-3 gap-3">
              {relatedProducts(product.slug).map((r) => (
                <Link key={r.slug} to={`/protection-products/${r.slug}`}
                      className="block bg-white border border-[var(--ssc-line)] rounded-xl p-4 hover:border-[var(--ssc-blue)] transition"
                      data-testid={`product-related-${r.slug}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <r.Icon size={16} className="text-[var(--ssc-blue)]" />
                    <div className="font-extrabold text-[var(--ssc-navy)] text-sm">{r.name}</div>
                  </div>
                  <p className="text-xs text-[var(--ssc-ink-2)] line-clamp-2">{r.shortDesc}</p>
                </Link>
              ))}
            </div>
          </section>

          {/* 10. FAQ */}
          <section className="ssc-card" data-testid="product-faqs">
            <h2 className="text-xl font-extrabold text-[var(--ssc-navy)]">FAQs</h2>
            <ul className="mt-4 space-y-4">
              {product.faqs.map((f, i) => (
                <li key={i}>
                  <div className="font-semibold text-[var(--ssc-navy)]">{f.q}</div>
                  <div className="text-sm text-[var(--ssc-ink-2)] mt-1">{f.a}</div>
                </li>
              ))}
            </ul>
          </section>

          {/* 11. Final CTA + bottom disclaimer */}
          <section className="text-center" data-testid="product-final-cta">
            <div className="flex flex-wrap justify-center gap-2">
              <button onClick={handleAddToDeal} className="ssc-btn ssc-btn-primary"
                      data-testid="product-add-to-deal-bottom">
                Add to Build My Deal
              </button>
              <Link to="/contact" className="ssc-btn ssc-btn-outline"
                    data-testid="product-ask-scott-bottom">
                <MessageSquareText size={16} /> Ask Scott About This Product
              </Link>
              <Link to="/protection-products" className="ssc-btn ssc-btn-outline"
                    data-testid="product-back-bottom">
                Back to Protection Products
              </Link>
            </div>
            <p className="text-xs text-[var(--ssc-ink-2)] mt-6 max-w-3xl mx-auto leading-relaxed">
              {PROTECTION_DISCLAIMER}
            </p>
            <p className="text-xs text-[var(--ssc-ink-2)] mt-3">
              Questions? <a href={siteConfig.scott.phoneTel} className="text-[var(--ssc-blue)] font-semibold">Call Scott at {siteConfig.scott.phone}</a>
            </p>
          </section>
        </div>
      </section>
    </main>
  );
}

// Keep the auto-imported lint silent if PROTECTION_PRODUCTS is unused at runtime
void PROTECTION_PRODUCTS;
